import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

export interface SubscriptionStatus {
  isElite: boolean;
  subscriptionStatus: 'active' | 'canceled' | 'past_due' | 'incomplete' | null;
  currentPeriodEnd: string | null;
  eliteSince: string | null;
}

export interface BillingInfo {
  status: 'none' | 'active' | 'cancelling' | 'past_due';
  eliteSince: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  paymentMethod: { brand: string; last4: string } | null;
  planAmount: number;
  planInterval: string;
}

export interface ReactivateResponse {
  success: boolean;
  status: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string;
}

export interface CheckoutSessionResponse {
  url: string;
}

export interface PortalSessionResponse {
  url: string;
}

export interface PortalSessionRequest {
  flow?: 'cancellation' | 'payment_method_update';
}

export interface BillingPaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export interface PaymentMethodsResponse {
  paymentMethods: BillingPaymentMethod[];
}

export interface AttachPaymentMethodRequest {
  paymentMethodId: string;
}

export interface AttachPaymentMethodResponse {
  success: boolean;
  paymentMethod: BillingPaymentMethod;
}

export interface CancelSubscriptionResponse {
  success: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string;
}

export interface SetupIntentResponse {
  clientSecret: string;
}

export interface BillingInvoice {
  id: string;
  number: string | null;
  status: string;
  amountDue: number;
  amountPaid: number;
  currency: string;
  created: string;
  hostedInvoiceUrl: string | null;
  pdfUrl: string | null;
}

export interface InvoicesResponse {
  invoices: BillingInvoice[];
  hasMore: boolean;
}

export interface InvoicesRequest {
  limit?: number;
  startingAfter?: string;
}

export const subscriptionApi = createApi({
  reducerPath: 'subscriptionApi',
  baseQuery: baseQuery,
  tagTypes: ['Subscription', 'PaymentMethods', 'Invoices'],
  endpoints: (builder) => ({
    getSubscriptionStatus: builder.query<SubscriptionStatus, void>({
      query: () => ({
        url: '/subscription/status',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Subscription'],
    }),

    getBilling: builder.query<BillingInfo, void>({
      query: () => ({
        url: '/subscription/billing',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Subscription'],
    }),

    createCheckoutSession: builder.mutation<CheckoutSessionResponse, { source?: string } | void>({
      query: (params) => ({
        url: '/subscription/create-checkout-session',
        method: 'POST',
        requiresAuth: true,
        ...(params && params.source ? { body: { source: params.source } } : {}),
      }),
    }),

    createPortalSession: builder.mutation<PortalSessionResponse, PortalSessionRequest | void>({
      query: (params) => ({
        url: '/subscription/create-portal-session',
        method: 'POST',
        requiresAuth: true,
        ...(params && { body: params }),
      }),
    }),

    reactivateSubscription: builder.mutation<ReactivateResponse, void>({
      query: () => ({
        url: '/subscription/reactivate',
        method: 'POST',
        requiresAuth: true,
      }),
      invalidatesTags: ['Subscription'],
    }),

    cancelSubscription: builder.mutation<CancelSubscriptionResponse, void>({
      query: () => ({
        url: '/billing/subscription/cancel',
        method: 'POST',
        requiresAuth: true,
      }),
      invalidatesTags: ['Subscription'],
    }),

    getPaymentMethods: builder.query<BillingPaymentMethod[], void>({
      query: () => ({
        url: '/billing/payment-methods',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['PaymentMethods'],
    }),

    createSetupIntent: builder.mutation<SetupIntentResponse, void>({
      query: () => ({
        url: '/billing/setup-intent',
        method: 'POST',
        requiresAuth: true,
      }),
    }),

    attachPaymentMethod: builder.mutation<AttachPaymentMethodResponse, AttachPaymentMethodRequest>({
      query: (body) => ({
        url: '/billing/payment-methods',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['PaymentMethods', 'Subscription'],
    }),

    removePaymentMethod: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/billing/payment-methods/${id}`,
        method: 'DELETE',
        requiresAuth: true,
      }),
      invalidatesTags: ['PaymentMethods'],
    }),

    setDefaultPaymentMethod: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/billing/payment-methods/${id}/default`,
        method: 'POST',
        requiresAuth: true,
      }),
      invalidatesTags: ['PaymentMethods', 'Subscription'],
    }),

    getInvoices: builder.query<InvoicesResponse, InvoicesRequest>({
      query: ({ limit = 10, startingAfter } = {}) => {
        const params = new URLSearchParams({ limit: limit.toString() });
        if (startingAfter) params.append('starting_after', startingAfter);
        return {
          url: `/billing/invoices?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['Invoices'],
    }),
  }),
});

export const {
  useGetSubscriptionStatusQuery,
  useGetBillingQuery,
  useCreateCheckoutSessionMutation,
  useCreatePortalSessionMutation,
  useReactivateSubscriptionMutation,
  useCancelSubscriptionMutation,
  useGetPaymentMethodsQuery,
  useCreateSetupIntentMutation,
  useAttachPaymentMethodMutation,
  useRemovePaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
  useGetInvoicesQuery,
} = subscriptionApi;
